<template>
    <div class="portal-page" :style="`direction:${lang.dir}`">
        <div class="portal-page__header" :class="{ 'portal-page__header--plain': loggedIn }">
            <template v-if="!loggedIn">
                <div class="portal-brand">
                    <i class="mdi mdi-wrench"></i>
                </div>
                <h1>{{ lang.technician_portal }}</h1>
                <p class="portal-page__subtitle">{{ lang.login_name }}</p>
            </template>
            <template v-else>
                <div class="portal-page__welcome">
                    <span class="portal-page__eyebrow">{{ lang.technician_portal }}</span>
                    <h1>{{ employeeName }}</h1>
                </div>
            </template>
        </div>

        <div class="portal-page__body">
            <!-- Not logged in -->
            <div v-if="!loggedIn" class="portal-card portal-block">
                <label><i class="mdi mdi-account-outline"></i> {{ lang.login_name }}</label>
                <b-form-input class="inborder portal-input" v-model="username" />
                <label><i class="mdi mdi-lock-outline"></i> {{ lang.password }}</label>
                <b-form-input class="inborder portal-input" v-model="password" type="password" @keyup.enter="login" />
                <div v-if="message" class="portal-message error">{{ message }}</div>
                <b-button variant="primary" block class="mt-3" :disabled="busy" @click="login">
                    {{ lang.login }}
                </b-button>
            </div>

            <!-- Logged in: my jobs -->
            <div v-else class="portal-block">
                <div v-if="message" class="portal-message error">{{ message }}</div>

                <div class="portal-rating-summary">
                    <div class="portal-rating-pill rating-excellent">
                        <span class="portal-rating-pill__num">{{ ratingSummary.counts.excellent }}</span>
                        <span class="portal-rating-pill__label">{{ lang.excellent }}</span>
                    </div>
                    <div class="portal-rating-pill rating-good">
                        <span class="portal-rating-pill__num">{{ ratingSummary.counts.good }}</span>
                        <span class="portal-rating-pill__label">{{ lang.good }}</span>
                    </div>
                    <div class="portal-rating-pill rating-bad">
                        <span class="portal-rating-pill__num">{{ ratingSummary.counts.bad }}</span>
                        <span class="portal-rating-pill__label">{{ lang.bad }}</span>
                    </div>
                </div>

                <h2 class="portal-section-title"><i class="mdi mdi-clipboard-list-outline"></i>{{ lang.my_jobs }}</h2>
                <div v-if="jobs.length === 0" class="portal-empty">{{ lang.no_jobs_assigned }}</div>
                <div v-for="j in jobs" :key="j.assignment_id" class="portal-job-card" :class="'accent-' + j.status">
                    <div class="portal-job-card__row">
                        <strong>{{ j.item_name }}</strong>
                        <span class="portal-status" :class="'status-' + j.status">{{ statusLabel(j.status) }}</span>
                    </div>
                    <div class="portal-job-card__meta"><i class="mdi mdi-account-circle-outline"></i>{{ j.customer_name }} — {{ j.car }}</div>
                    <div class="portal-job-card__meta"><i class="mdi mdi-pound"></i>{{ j.card_display_number }}</div>
                    <div v-if="j.status === 'finished' && j.rating" class="portal-job-card__meta">
                        {{ lang.rating }}: <span class="portal-rating-tag" :class="'rating-' + j.rating">{{ ratingLabel(j.rating) }}</span>
                    </div>
                    <div v-if="j.status === 'paused' && j.pause_reason" class="portal-job-card__meta portal-pause-reason">
                        <i class="mdi mdi-information-outline"></i>{{ j.pause_reason }}
                    </div>

                    <!-- Pause reason prompt, shown in place of the action row -->
                    <div v-if="pausingAssignmentId === j.assignment_id" class="portal-pause-prompt">
                        <label>{{ lang.pause_reason }}</label>
                        <b-form-textarea class="inborder portal-input" v-model="pauseReasonText" rows="2" :placeholder="lang.pause_reason" />
                        <div class="portal-job-card__actions mt-2">
                            <b-button size="sm" variant="warning" :disabled="busyJob === j.assignment_id" @click="confirmPause(j)"><i class="mdi mdi-pause"></i>{{ lang.pause }}</b-button>
                            <b-button size="sm" variant="outline-secondary" @click="pausingAssignmentId = 0">{{ lang.cancel }}</b-button>
                        </div>
                    </div>
                    <div v-else class="portal-job-card__actions">
                        <b-button v-if="j.status === 'assigned'" size="sm" variant="primary" :disabled="busyJob === j.assignment_id" @click="doAction('startJob', j)"><i class="mdi mdi-play"></i>{{ lang.start }}</b-button>
                        <template v-if="j.status === 'in_progress'">
                            <b-button size="sm" variant="warning" :disabled="busyJob === j.assignment_id" @click="openPausePrompt(j)"><i class="mdi mdi-pause"></i>{{ lang.pause }}</b-button>
                            <b-button size="sm" variant="success" :disabled="busyJob === j.assignment_id" @click="doAction('finishJob', j)"><i class="mdi mdi-check"></i>{{ lang.finish }}</b-button>
                        </template>
                        <template v-if="j.status === 'paused'">
                            <b-button size="sm" variant="primary" :disabled="busyJob === j.assignment_id" @click="doAction('startJob', j)"><i class="mdi mdi-play"></i>{{ lang.resume }}</b-button>
                            <b-button size="sm" variant="success" :disabled="busyJob === j.assignment_id" @click="doAction('finishJob', j)"><i class="mdi mdi-check"></i>{{ lang.finish }}</b-button>
                        </template>
                        <b-button size="sm" variant="outline-secondary" @click="toggleHistory(j)">
                            <i class="mdi mdi-message-text-outline"></i>{{ lang.job_history }}
                        </b-button>
                        <b-button size="sm" variant="outline-secondary" @click="toggleParts(j)">
                            <i class="mdi mdi-cog-outline"></i>{{ lang.spare_parts }}
                        </b-button>
                        <b-button size="sm" variant="outline-secondary" @click="toggleAttachments(j)">
                            <i class="mdi mdi-camera-outline"></i>{{ lang.attachement }}
                        </b-button>
                    </div>

                    <!-- Attachments panel -->
                    <div v-if="openAttachAssignmentId === j.assignment_id" class="portal-history-panel">
                        <div v-if="attachLoading" class="portal-empty">{{ lang.loading }}</div>
                        <div v-else-if="attachments.length === 0" class="portal-empty">{{ lang.no_records }}</div>
                        <div v-else class="portal-attach-grid">
                            <a v-for="f in attachments" :key="f.id" :href="attachUrl(f)" target="_blank" class="portal-attach-thumb">
                                <img v-if="isImage(f.file_type)" :src="attachUrl(f)" :alt="f.file_name" />
                                <i v-else class="mdi mdi-file-document-outline"></i>
                                <span>{{ f.file_name }}</span>
                            </a>
                        </div>
                        <input type="file" ref="attachInput" multiple accept="image/*" class="portal-attach-input" @change="onAttachFilesSelected($event, j)" />
                        <b-button size="sm" variant="primary" class="mt-2" :disabled="uploadingAttach" @click="$refs.attachInput.click()">
                            <i class="mdi mdi-camera-plus-outline"></i>{{ lang.add_photo }}
                        </b-button>
                    </div>

                    <!-- Spare parts panel -->
                    <div v-if="openPartsAssignmentId === j.assignment_id" class="portal-history-panel">
                        <div v-if="partsLoading" class="portal-empty">{{ lang.loading }}</div>
                        <div v-else>
                            <div v-if="partRequests.length === 0" class="portal-empty">{{ lang.no_records }}</div>
                            <div v-for="req in partRequests" :key="req.id" class="portal-part-request">
                                <div class="portal-part-request__row">
                                    <strong>{{ req.item_name }}</strong>
                                    <span class="portal-status" :class="'part-status-' + req.status">{{ partStatusLabel(req.status) }}</span>
                                </div>
                                <div class="portal-job-card__meta">{{ lang.qtty }}: {{ req.quantity }}</div>
                                <div v-if="canConfirmPart(req)" class="portal-job-card__actions">
                                    <b-button size="sm" variant="success" @click="decidePart(req, 'fulfilled', j)"><i class="mdi mdi-check"></i>{{ lang.received }}</b-button>
                                    <b-button size="sm" variant="outline-danger" @click="decidePart(req, 'rejected', j)">{{ lang.decline }}</b-button>
                                </div>
                            </div>
                        </div>
                        <div class="portal-part-compose">
                            <b-form-input class="inborder portal-input" v-model="newPartName" :placeholder="lang.item_name" />
                            <b-form-input class="inborder portal-input portal-part-qty" v-model="newPartQty" type="number" min="1" />
                            <b-button size="sm" variant="primary" :disabled="sendingPart" @click="submitPartRequest(j)"><i class="mdi mdi-plus"></i></b-button>
                        </div>
                    </div>

                    <!-- History / chat panel -->
                    <div v-if="openHistoryAssignmentId === j.assignment_id" class="portal-history-panel">
                        <div v-if="historyLoading" class="portal-empty">{{ lang.loading }}</div>
                        <div v-else-if="history.length === 0" class="portal-empty">{{ lang.no_records }}</div>
                        <div v-else class="portal-history-feed">
                            <div v-for="(h,i) in history" :key="i" class="portal-history-item" :class="'kind-' + h.kind">
                                <i class="mdi" :class="historyIcon(h.event_type)"></i>
                                <div class="portal-history-item__body">
                                    <div class="portal-history-item__meta">
                                        <strong>{{ h.actor_name || lang.system }}</strong>
                                        <span>{{ h.created_date }}</span>
                                    </div>
                                    <div>{{ h.text }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="portal-history-compose">
                            <b-form-input class="inborder portal-input" v-model="newNoteText" :placeholder="lang.write_a_message" @keyup.enter="sendNote(j)" />
                            <b-button size="sm" variant="primary" :disabled="sendingNote" @click="sendNote(j)"><i class="mdi mdi-send"></i></b-button>
                        </div>
                    </div>
                </div>

                <b-button variant="outline-danger" block class="mt-4" @click="logout">
                    <i class="mdi mdi-logout"></i> {{ lang.logout }}
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'

const COOKIE_NAME = 'tech_portal_auth';

export default {
    name: 'PortalTechLogin',
    data() {
        return {
            username: '',
            password: '',
            authCode: '',
            busy: false,
            message: '',
            employeeName: '',
            jobs: [],
            ratingSummary: { counts: { excellent: 0, good: 0, bad: 0 }, total_rated: 0 },
            busyJob: 0,
            pausingAssignmentId: 0,
            pauseReasonText: '',
            openHistoryAssignmentId: 0,
            history: [],
            historyLoading: false,
            newNoteText: '',
            sendingNote: false,
            openPartsAssignmentId: 0,
            partRequests: [],
            partsLoading: false,
            newPartName: '',
            newPartQty: 1,
            sendingPart: false,
            openAttachAssignmentId: 0,
            attachments: [],
            attachBaseUrl: '',
            attachLoading: false,
            uploadingAttach: false,
        }
    },
    computed: {
        loggedIn(){
            return !!this.authCode;
        },
        lang: function(){
            if(this.$cookies.get(this.$store.state.LangCooki) != null){
                if(this.$cookies.get(this.$store.state.LangCooki) == 'en'){
                    return this.$store.state.lang.en;
                }else if(this.$cookies.get(this.$store.state.LangCooki) == 'ur'){
                    return this.$store.state.lang.ur;
                }else{
                    return this.$store.state.lang.ar;
                }
            }
            else if(this.$store.state.deflang == 'en'){
                return this.$store.state.lang.en;
            }
            else if(this.$store.state.deflang == 'ur'){
                return this.$store.state.lang.ur;
            }else{
                return this.$store.state.lang.ar;
            }
        },
        isEn(){
            return this.lang.langname === 'en';
        },
        myUserId(){
            return Number((this.authCode || '').split('||')[0] || 0);
        },
    },
    methods: {
        statusLabel(status){
            const en = { assigned: 'Assigned', in_progress: 'In Progress', paused: 'Paused', finished: 'Finished' };
            const ar = { assigned: 'بانتظار البدء', in_progress: 'قيد التنفيذ', paused: 'متوقف مؤقتاً', finished: 'منتهي' };
            const map = this.isEn ? en : ar;
            return map[status] || status;
        },
        ratingLabel(rating){
            const en = { excellent: 'Excellent', good: 'Good', bad: 'Bad' };
            const ar = { excellent: 'ممتاز', good: 'جيد', bad: 'سيء' };
            const map = this.isEn ? en : ar;
            return map[rating] || rating;
        },
        doAction(type, job, extra = {}){
            this.busyJob = job.assignment_id;
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', type);
            post.append('auth', this.authCode);
            post.append('data[assignment_id]', job.assignment_id);
            Object.keys(extra).forEach((k) => post.append(`data[${k}]`, extra[k]));
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                this.busyJob = 0;
                const d = res.data;
                if(d.error && d.error.number === 200){
                    this.loadMyJobs();
                }else if(d.error && d.error.message === 'pause_reason_required'){
                    this.message = this.isEn ? 'Please enter a reason before pausing.' : 'الرجاء إدخال سبب الإيقاف المؤقت.';
                }else{
                    this.message = this.isEn ? 'Could not update this job. Please refresh and try again.' : 'تعذر تحديث هذه المهمة. الرجاء تحديث الصفحة والمحاولة مجدداً.';
                }
            }).catch(() => { this.busyJob = 0; });
        },
        openPausePrompt(job){
            this.pausingAssignmentId = job.assignment_id;
            this.pauseReasonText = '';
        },
        confirmPause(job){
            if(!this.pauseReasonText.trim()){
                this.message = this.isEn ? 'Please enter a reason before pausing.' : 'الرجاء إدخال سبب الإيقاف المؤقت.';
                return;
            }
            this.doAction('pauseJob', job, { reason: this.pauseReasonText.trim() });
            this.pausingAssignmentId = 0;
        },
        historyIcon(eventType){
            const icons = {
                started: 'mdi-play-circle-outline',
                resumed: 'mdi-play-circle-outline',
                paused: 'mdi-pause-circle-outline',
                finished: 'mdi-check-circle-outline',
                note: 'mdi-message-text-outline',
                spare_part_requested: 'mdi-cog-outline',
            };
            return icons[eventType] || 'mdi-circle-small';
        },
        toggleHistory(job){
            if(this.openHistoryAssignmentId === job.assignment_id){
                this.openHistoryAssignmentId = 0;
                return;
            }
            this.openHistoryAssignmentId = job.assignment_id;
            this.loadHistory(job);
        },
        loadHistory(job){
            this.historyLoading = true;
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'getJobCardHistoryTech');
            post.append('auth', this.authCode);
            post.append('data[cardid]', job.card_id);
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                this.historyLoading = false;
                const d = res.data;
                if(d.error && d.error.number === 200){
                    this.history = d.results;
                }
            }).catch(() => { this.historyLoading = false; });
        },
        sendNote(job){
            if(!this.newNoteText.trim()){ return; }
            this.sendingNote = true;
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'sendJobCardNoteTech');
            post.append('auth', this.authCode);
            post.append('data[cardid]', job.card_id);
            post.append('data[body]', this.newNoteText.trim());
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                this.sendingNote = false;
                if(res.data.error && res.data.error.number === 200){
                    this.newNoteText = '';
                    this.loadHistory(job);
                }
            }).catch(() => { this.sendingNote = false; });
        },
        partStatusLabel(status){
            const en = { pending: 'Pending', approved: 'Approved', ordered: 'Ordered', fulfilled: 'Received', rejected: 'Rejected' };
            const ar = { pending: 'قيد الانتظار', approved: 'تمت الموافقة', ordered: 'تم الطلب', fulfilled: 'تم الاستلام', rejected: 'مرفوض' };
            const map = this.isEn ? en : ar;
            return map[status] || status;
        },
        canConfirmPart(req){
            return Number(req.requested_by_id) === this.myUserId && ['approved', 'ordered'].includes(req.status);
        },
        toggleParts(job){
            if(this.openPartsAssignmentId === job.assignment_id){
                this.openPartsAssignmentId = 0;
                return;
            }
            this.openPartsAssignmentId = job.assignment_id;
            this.loadPartRequests(job);
        },
        loadPartRequests(job){
            this.partsLoading = true;
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'get_requests_list');
            post.append('auth', this.authCode);
            post.append('data[job_card_id]', job.card_id);
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                this.partsLoading = false;
                const d = res.data;
                if(d.error && d.error.number === 200){
                    this.partRequests = (d.results && d.results.data) ? d.results.data : [];
                }
            }).catch(() => { this.partsLoading = false; });
        },
        submitPartRequest(job){
            const itemName = (this.newPartName || '').trim();
            const qty = Number(this.newPartQty || 0);
            if(!itemName || qty <= 0){ return; }
            this.sendingPart = true;
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'create_part_request');
            post.append('auth', this.authCode);
            post.append('data[job_card_id]', job.card_id);
            post.append('data[item_name]', itemName);
            post.append('data[quantity]', qty);
            post.append('data[requested_by_id]', this.myUserId);
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                this.sendingPart = false;
                if(res.data.error && res.data.error.number === 200){
                    this.newPartName = '';
                    this.newPartQty = 1;
                    this.loadPartRequests(job);
                    this.loadMyJobs();
                }else{
                    this.message = this.isEn ? 'Could not submit the spare part request.' : 'تعذر إرسال طلب قطعة الغيار.';
                }
            }).catch(() => { this.sendingPart = false; });
        },
        decidePart(req, status, job){
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'update_request_status');
            post.append('auth', this.authCode);
            post.append('data[request_id]', req.id);
            post.append('data[status]', status);
            post.append('data[requester_action]', 1);
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                if(res.data.error && res.data.error.number === 200){
                    this.loadPartRequests(job);
                }
            });
        },
        isImage(fileType){
            return (fileType || '').indexOf('image') === 0;
        },
        attachUrl(file){
            return `${window.location.origin}/api/uploads/cards/${file.docid}/${file.file_name}`;
        },
        toggleAttachments(job){
            if(this.openAttachAssignmentId === job.assignment_id){
                this.openAttachAssignmentId = 0;
                return;
            }
            this.openAttachAssignmentId = job.assignment_id;
            this.loadAttachments(job);
        },
        loadAttachments(job){
            this.attachLoading = true;
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'getCardFile');
            post.append('auth', this.authCode);
            post.append('data[cardid]', job.card_id);
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                this.attachLoading = false;
                const d = res.data;
                if(d.error && d.error.number === 200){
                    const files = (d.results.data.files && d.results.data.files.results) ? d.results.data.files.results : [];
                    this.attachments = files.map((f) => Object.assign({}, f, { docid: job.card_id }));
                }
            }).catch(() => { this.attachLoading = false; });
        },
        onAttachFilesSelected(event, job){
            const files = event.target.files;
            if(!files || !files.length){ return; }
            this.uploadingAttach = true;
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'uploadFile');
            post.append('auth', this.authCode);
            post.append('data[type]', 1);
            post.append('data[cardid]', job.card_id);
            for(let i=0;i<files.length;i++){
                post.append(`data[files][${i}]`, files[i]);
            }
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                this.uploadingAttach = false;
                event.target.value = '';
                if(res.data.error && res.data.error.number === 200){
                    this.loadAttachments(job);
                }else{
                    this.message = this.isEn ? 'Could not upload the file.' : 'تعذر رفع الملف.';
                }
            }).catch(() => { this.uploadingAttach = false; event.target.value = ''; });
        },
        loginErrorMessage(){
            const en = 'Incorrect username or password.';
            const ar = 'اسم الدخول أو كلمة المرور غير صحيحة.';
            return this.isEn ? en : ar;
        },
        login(){
            if(!this.username || !this.password){ return; }
            this.busy = true;
            this.message = '';
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'register');
            post.append('data[username]', this.username);
            post.append('data[password]', this.password);
            post.append('data[defBranch]', '1');
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                this.busy = false;
                const d = res.data;
                if(d.error && d.error.number === 200){
                    const code = d.results.data.ID + '||' + d.results.hash + '||portal||' + d.results.time + '||' + this.lang.langname + '||1';
                    this.authCode = code;
                    localStorage.setItem(COOKIE_NAME, code);
                    this.password = '';
                    this.loadMyJobs();
                }else{
                    this.message = this.loginErrorMessage();
                }
            }).catch(() => {
                this.busy = false;
                this.message = this.loginErrorMessage();
            });
        },
        loadMyJobs(){
            const post = new FormData();
            post.append('defBranch', '1');
            post.append('financeYear', String(new Date().getFullYear()));
            post.append('type', 'getMyJobs');
            post.append('auth', this.authCode);
            axios.post(this.$store.state.SAMCOTEC.r_path, post).then((res) => {
                const d = res.data;
                if(d.error && d.error.number === 200){
                    this.employeeName = d.results.employee_name;
                    this.jobs = d.results.jobs;
                    this.ratingSummary = d.results.rating_summary;
                }else if(d.error && d.error.number === 404 && d.error.message === 'not_linked_to_employee'){
                    this.message = this.isEn
                        ? 'This account is not linked to an employee record. Contact your administrator.'
                        : 'هذا الحساب غير مرتبط بسجل موظف. الرجاء التواصل مع الإدارة.';
                }else{
                    this.doLogoutLocally();
                }
            }).catch(() => {
                this.doLogoutLocally();
            });
        },
        logout(){
            this.doLogoutLocally();
        },
        doLogoutLocally(){
            localStorage.removeItem(COOKIE_NAME);
            this.authCode = '';
            this.username = '';
            this.jobs = [];
            this.employeeName = '';
            this.ratingSummary = { counts: { excellent: 0, good: 0, bad: 0 }, total_rated: 0 };
            this.pausingAssignmentId = 0;
            this.openHistoryAssignmentId = 0;
            this.history = [];
            this.openPartsAssignmentId = 0;
            this.partRequests = [];
            this.openAttachAssignmentId = 0;
            this.attachments = [];
        },
    },
    created(){
        const saved = localStorage.getItem(COOKIE_NAME);
        if(saved){
            this.authCode = saved;
            this.loadMyJobs();
        }
    },
}
</script>

<style scoped>
.portal-page {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

/* ---- Header ---- */
.portal-page__header {
    padding: 40px 24px 28px;
    background: linear-gradient(160deg, var(--portal-ink) 0%, #1e3247 100%);
    color: #fff;
    text-align: center;
    border-radius: 0 0 28px 28px;
}
.portal-page__header--plain {
    text-align: start;
    padding: 28px 24px 20px;
    border-radius: 0 0 24px 24px;
}
.portal-brand {
    width: 56px;
    height: 56px;
    margin: 0 auto 14px;
    border-radius: 16px;
    background: var(--portal-accent);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 18px -6px rgba(221, 126, 31, .55);
}
.portal-brand .mdi {
    font-size: 30px;
    color: var(--portal-accent-ink);
}
.portal-page__header h1 {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
}
.portal-page__subtitle {
    margin: 6px 0 0;
    font-size: .85rem;
    color: rgba(255, 255, 255, .68);
}
.portal-page__eyebrow {
    display: block;
    font-size: .7rem;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--portal-accent);
    font-weight: 700;
    margin-bottom: 4px;
}

/* ---- Body & generic blocks ---- */
.portal-page__body {
    flex: 1;
    padding: 20px 18px 28px;
}
.portal-card {
    background: var(--portal-surface);
    border: 1px solid var(--portal-border);
    border-radius: var(--portal-radius-lg);
    box-shadow: var(--portal-shadow);
    padding: 18px 16px;
}
.portal-block label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;
    margin-bottom: 6px;
    font-size: .78rem;
    font-weight: 600;
    color: var(--portal-ink-muted);
}
.portal-block label:first-child { margin-top: 0; }
.portal-input {
    background: var(--portal-surface-2) !important;
    border: 1px solid var(--portal-border) !important;
    border-radius: var(--portal-radius-md) !important;
    padding: 10px 12px !important;
    color: var(--portal-ink) !important;
}
.portal-input:focus {
    border-color: var(--portal-accent) !important;
    box-shadow: 0 0 0 3px var(--portal-accent-soft) !important;
}
.portal-block >>> .btn-primary {
    background: var(--portal-accent) !important;
    border-color: var(--portal-accent) !important;
    color: var(--portal-accent-ink) !important;
    font-weight: 700;
    border-radius: var(--portal-radius-md) !important;
    padding: 11px !important;
}
.portal-block >>> .btn-primary:hover {
    background: var(--portal-accent-dark) !important;
    border-color: var(--portal-accent-dark) !important;
}
.portal-block >>> .btn-outline-danger {
    color: var(--portal-bad) !important;
    border-color: var(--portal-bad) !important;
    border-radius: var(--portal-radius-md) !important;
    font-weight: 600;
}
.portal-block >>> .btn-outline-danger:hover {
    background: var(--portal-bad-soft) !important;
}
.portal-message {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: var(--portal-radius-md);
    font-size: .85rem;
    font-weight: 500;
}
.portal-message.error {
    background: var(--portal-bad-soft);
    color: var(--portal-bad);
}
.portal-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: .82rem;
    font-weight: 700;
    letter-spacing: .03em;
    text-transform: uppercase;
    color: var(--portal-ink-muted);
    margin: 4px 0 10px 4px;
}
.portal-section-title .mdi { color: var(--portal-accent); font-size: 1.05rem; }

.portal-empty {
    color: var(--portal-ink-muted);
    padding: 24px 0;
    text-align: center;
    font-size: .88rem;
}

/* ---- Job cards ---- */
.portal-job-card {
    background: var(--portal-surface);
    border: 1px solid var(--portal-border);
    border-inline-start: 4px solid var(--portal-border);
    border-radius: var(--portal-radius-lg);
    box-shadow: var(--portal-shadow);
    padding: 14px;
    margin-bottom: 12px;
}
.portal-job-card.accent-in_progress { border-inline-start-color: var(--portal-accent); }
.portal-job-card.accent-paused { border-inline-start-color: var(--portal-warn); }
.portal-job-card.accent-finished { border-inline-start-color: var(--portal-good); }
.portal-job-card__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.portal-job-card__row strong { color: var(--portal-ink); }
.portal-job-card__meta {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: .8rem;
    color: var(--portal-ink-muted);
    margin-top: 4px;
}
.portal-job-card__meta .mdi { font-size: .95rem; }
.portal-job-card__actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
}
.portal-status {
    font-size: .7rem;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: var(--portal-radius-pill);
    color: #fff;
    white-space: nowrap;
}
.status-assigned { background: #8fa1b3; }
.status-in_progress { background: var(--portal-accent); }
.status-paused { background: var(--portal-warn); }
.status-finished { background: var(--portal-good); }

/* ---- Rating summary ---- */
.portal-rating-summary {
    display: flex;
    gap: 8px;
    margin-bottom: 18px;
}
.portal-rating-pill {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 4px;
    border-radius: var(--portal-radius-md);
    background: var(--portal-surface);
    border: 1px solid var(--portal-border);
    box-shadow: var(--portal-shadow);
}
.portal-rating-pill__num {
    font-size: 1.3rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
}
.portal-rating-pill__label {
    font-size: .68rem;
    font-weight: 600;
    color: var(--portal-ink-muted);
    margin-top: 2px;
}
.portal-rating-pill.rating-excellent .portal-rating-pill__num { color: var(--portal-accent-dark); }
.portal-rating-pill.rating-good .portal-rating-pill__num { color: var(--portal-good); }
.portal-rating-pill.rating-bad .portal-rating-pill__num { color: var(--portal-bad); }
.portal-rating-tag { font-weight: 700; }
.portal-rating-tag.rating-excellent { color: var(--portal-accent-dark); }
.portal-rating-tag.rating-good { color: var(--portal-good); }
.portal-rating-tag.rating-bad { color: var(--portal-bad); }

/* ---- Pause reason ---- */
.portal-pause-reason {
    color: var(--portal-warn);
}
.portal-pause-prompt {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed var(--portal-border);
}
.portal-pause-prompt label {
    display: block;
    font-size: .78rem;
    font-weight: 600;
    color: var(--portal-ink-muted);
    margin-bottom: 4px;
}

/* ---- History / chat panel ---- */
.portal-history-panel {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed var(--portal-border);
}
.portal-history-feed {
    max-height: 220px;
    overflow-y: auto;
    margin-bottom: 10px;
}
.portal-history-item {
    display: flex;
    gap: 8px;
    padding: 6px 0;
    font-size: .82rem;
}
.portal-history-item .mdi {
    color: var(--portal-ink-muted);
    font-size: 1.1rem;
    margin-top: 2px;
}
.portal-history-item.kind-note .mdi { color: var(--portal-accent); }
.portal-history-item__meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: .7rem;
    color: var(--portal-ink-muted);
}
.portal-history-item__meta strong { color: var(--portal-ink); }
.portal-history-compose {
    display: flex;
    gap: 8px;
}
.portal-history-compose .portal-input {
    flex: 1;
}

/* ---- Spare parts panel ---- */
.portal-part-request {
    border-bottom: 1px solid var(--portal-border);
    padding: 8px 0;
    font-size: .85rem;
}
.portal-part-request:last-of-type { border-bottom: none; }
.portal-part-request__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.portal-status.part-status-pending { background: var(--portal-warn); color: #fff; }
.portal-status.part-status-approved,
.portal-status.part-status-ordered { background: var(--portal-accent); color: #fff; }
.portal-status.part-status-fulfilled { background: var(--portal-good); color: #fff; }
.portal-status.part-status-rejected { background: var(--portal-bad); color: #fff; }
.portal-part-compose {
    display: flex;
    gap: 8px;
    margin-top: 10px;
}
.portal-part-compose .portal-input {
    flex: 1;
}
.portal-part-qty {
    max-width: 70px;
}

/* ---- Attachments panel ---- */
.portal-attach-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 10px;
}
.portal-attach-thumb {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1;
    border: 1px solid var(--portal-border);
    border-radius: var(--portal-radius-md);
    overflow: hidden;
    background: var(--portal-surface-2);
    text-decoration: none;
    color: var(--portal-ink-muted);
    font-size: .65rem;
    padding: 4px;
    text-align: center;
}
.portal-attach-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.portal-attach-thumb .mdi {
    font-size: 1.8rem;
    color: var(--portal-ink-muted);
}
.portal-attach-input {
    display: none;
}
</style>
